import React, { ReactElement, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  Container,
  Divider,
  makeStyles,
  Stepper,
  Step as StepperStep,
  StepLabel,
  Typography
} from '@material-ui/core';
import CREATE_CLASS from 'graph/classroom/create-class-mutation';
import GET_ORGANIZATIONS from 'graph/organization/get-organizations-query';
import { Formik, FormikProps, FormikValues, FormikTouched } from 'formik';
import useStore from 'store';
import { SourceKind, Organization } from 'types/schema-generated-types';
import * as Yup from 'yup';
import {
  Step,
  CreateClassroomForm,
  FormIndex,
  LicenseAssignFn,
  LicenseUnassignFn
} from '../../../../types';
import buildClassCreateInput from '../../../../util/build-create-class-input';
import DEFAULT_FORM from '../../../../util/default-form';
import {
  AddStudentsStep,
  AssignProductsStep,
  ConfirmationStep,
  CreateClassStep,
  formReducer,
  ActionType as FormActionType
} from '../../../components/Steps';
import getStudentValidation from '../../utils/student-form-validation';
import useDefaultGroupSettings from 'components/ClassroomsModule/hooks/use-default-group-settings';

const labels: { [key: string]: string } = {
  CREATE_CLASS: 'CREATE CLASS',
  ADD_STUDENTS: 'ADD STUDENTS',
  ASSIGN_PRODUCTS: 'ASSIGN PRODUCTS',
  REVIEW: 'REVIEW & CONFIRM'
};

const getStepInfo = (type?: SourceKind): Step[] => [
  {
    id: 'CREATE_CLASS',
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Field is required')
        .max(100),
      organization: Yup.string().required('Field is required'),
      educator: Yup.string().required('Field is required'),
      schoolYear: Yup.string().required('Field is required'),
      coeducators: Yup.array().of(Yup.string())
    }),
    validateOnChange: true,
    validateOnMount: true
  },
  {
    id: 'ADD_STUDENTS',
    validate: getStudentValidation(type),
    validateOnChange: true,
    validateOnBlur: true
  },
  {
    id: 'ASSIGN_PRODUCTS',
    validationSchema: Yup.object({
      students: Yup.array()
    })
  },
  {
    id: 'REVIEW'
  }
];

interface CreateClassroomModalFormProps {
  onClose: Function;
}

const formIndexes: { [key: number]: FormIndex } = {
  0: 'CREATE_CLASS',
  1: 'ADD_STUDENTS',
  2: 'ADD_STUDENTS'
};

const CreateClassroomModal = ({ onClose }: CreateClassroomModalFormProps) => {
  const classes = useStyles();
  const { t } = useTranslation('CreateClassroomModal');
  const [{ user }] = useStore();
  const { getProductCurriculumDefaultGroup } = useDefaultGroupSettings();
  const [{ activeStep, form }, dispatch] = useReducer(formReducer, {
    activeStep: 0,
    stepCount: 4,
    form: DEFAULT_FORM
  });

  const { data, loading } = useQuery(GET_ORGANIZATIONS);
  const [createClassMutation] = useMutation(CREATE_CLASS);

  const organizations: Organization[] = data?.organizations ?? [];
  const currentOrganization = useMemo(
    () =>
      data?.organizations.find(({ id }: Organization) => id === +form.CREATE_CLASS?.organization),
    [data, form.CREATE_CLASS]
  );

  const steps = getStepInfo(currentOrganization?.externalId?.sourceKind);
  const isFirstStep = useMemo(() => activeStep === 0, [activeStep]);
  const isLastStep = useMemo(() => activeStep === steps.length - 1, [activeStep, steps]);

  useEffect(
    () => {
      if (activeStep === 1 && form.ADD_STUDENTS) {
        const loginType = form.CREATE_CLASS.secretCodeType;
        const students = form.ADD_STUDENTS.students;

        dispatch({
          type: FormActionType.UPDATE_FORM,
          payload: {
            form: {
              ...form,
              ADD_STUDENTS: {
                ...students,
                students: students.map(student => {
                  if (!student.secretCode || !student.secretCode.code) {
                    return { ...student, secretCode: { code: '', loginType } };
                  }
                  return student;
                })
              }
            }
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStep]
  );

  useEffect(() => {
    if (activeStep === 0 && data && user.me && form.CREATE_CLASS) {
      const { organization, educator } = form.CREATE_CLASS;
      if (!organization && !educator) {
        dispatch({
          type: FormActionType.UPDATE_FORM,
          payload: {
            form: {
              ...form,
              CREATE_CLASS: {
                ...form.CREATE_CLASS,
                organization: data.organizations[0].id,
                educator: user.me.id.toString()
              }
            }
          }
        });
      }
    }
  }, [activeStep, data, user, form]);

  const createClass = useCallback(
    async (values: CreateClassroomForm): Promise<void> => {
      const data = buildClassCreateInput(values, [currentOrganization]);
      await createClassMutation({ variables: { classroom: data } });
      onClose(true);
    },
    [createClassMutation, currentOrganization, onClose]
  );

  const closeModal = useCallback((): void => {
    dispatch({ type: FormActionType.RESET_FORM });
    onClose();
  }, [dispatch, onClose]);

  const formikStepProps = React.useCallback(() => {
    const { ...formProps } = steps[activeStep];

    let selectedForm;
    if (isLastStep) {
      selectedForm = form;
    } else {
      selectedForm = form[formIndexes[activeStep]] ?? DEFAULT_FORM[formIndexes[activeStep]];
    }

    return { initialValues: selectedForm, onSubmit: () => {}, ...formProps };
  }, [form, activeStep, steps, isLastStep]);

  const getStep = React.useCallback(
    (onBlur: React.FocusEventHandler<HTMLDivElement>): React.ReactElement | null => {
      const handleLicenseAssign: LicenseAssignFn = (
        productFamilyId,
        productCurriculumId,
        studentId
      ) => {
        dispatch({
          type: FormActionType.ASSIGN_LICENSE,
          payload: { productFamilyId, productCurriculumId, studentId }
        });

        const gradeLevel = form.ADD_STUDENTS.students[studentId]?.gradeLevel ?? '';
        const group = getProductCurriculumDefaultGroup([productCurriculumId], gradeLevel);

        if (group) {
          dispatch({
            type: FormActionType.ADD_GROUP,
            payload: { studentId, group }
          });
        }
      };

      const handleLicenseUnassign: LicenseUnassignFn = (
        productFamilyId,
        productCurriculumId,
        studentId
      ) => {
        dispatch({
          type: FormActionType.UNASSIGN_LICENSE,
          payload: { productFamilyId, productCurriculumId, studentId }
        });

        const gradeLevel = form.ADD_STUDENTS.students[studentId]?.gradeLevel ?? '';
        const group = getProductCurriculumDefaultGroup([productCurriculumId], gradeLevel);

        if (group?.name === form.ADD_STUDENTS.students[studentId]?.group?.name) {
          dispatch({
            type: FormActionType.REMOVE_GROUP,
            payload: { studentId }
          });
        }
      };

      switch (activeStep) {
        case 0:
          return <CreateClassStep index={1} organizations={organizations} onBlur={onBlur} />;
        case 1:
          return <AddStudentsStep index={2} />;
        case 2:
          return (
            <AssignProductsStep
              index={3}
              organizationId={+form.CREATE_CLASS?.organization}
              onLicenseAssign={handleLicenseAssign}
              onLicenseUnassign={handleLicenseUnassign}
            />
          );
        case 3:
          return (
            <ConfirmationStep
              index={4}
              organizations={organizations}
              onLicenseAssign={handleLicenseAssign}
              onLicenseUnassign={handleLicenseUnassign}
            />
          );
        default:
          return null;
      }
    },
    [activeStep, form, getProductCurriculumDefaultGroup, organizations]
  );

  if (loading) return null;

  return (
    <Formik enableReinitialize {...formikStepProps()}>
      {(formik: FormikProps<FormikValues>): ReactElement => {
        const {
          submitForm,
          isSubmitting,
          isValid,
          values,
          validateForm,
          setErrors,
          setTouched
        } = formik;

        const validateAndSetTouched = async (): Promise<void> => {
          const validationErrors = await validateForm();
          if (validationErrors && Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            setTouched(validationErrors as FormikTouched<FormikValues>);
          }
        };

        const onBlur: React.FocusEventHandler<HTMLDivElement> = async () => {
          validateAndSetTouched();
        };

        const handleCancelClick = async () => {
          if (isFirstStep) {
            closeModal();
            return;
          }

          if (isLastStep) {
            const validationErrors = await validateForm();
            if (validationErrors && Object.keys(validationErrors).length) {
              setErrors(validationErrors);
              setTouched(validationErrors as FormikTouched<FormikValues>);
              return;
            }
          }

          dispatch({
            type: FormActionType.GO_BACK,
            payload: { form: { ...form, [formIndexes[activeStep]]: values } }
          });
        };

        const handleForwardClick = async (): Promise<void> => {
          if (isLastStep) {
            submitForm();
            createClass(values as CreateClassroomForm);
            closeModal();
            return;
          }

          const validationErrors = await validateForm();
          if (validationErrors && Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            setTouched(validationErrors as FormikTouched<FormikValues>);
            return;
          }

          dispatch({
            type: FormActionType.GO_FORWARD,
            payload: { form: { ...form, [formIndexes[activeStep]]: values } }
          });
        };

        return (
          <div className={classes.root}>
            <div className={classes.body}>
              <div className={classes.header}>
                <Typography variant="h2" component="h1">
                  {t('Create Class')}
                </Typography>
              </div>
              <Container>
                <Stepper activeStep={activeStep} className={classes.stepper}>
                  {steps.map(step => {
                    return (
                      <StepperStep key={step.id}>
                        <StepLabel
                          StepIconProps={{
                            classes: { active: classes.stepIcon, completed: classes.stepIcon }
                          }}
                          classes={{
                            label: classes.stepLabel,
                            active: classes.stepLabelActive
                          }}
                        >
                          {t(labels[step.id])}
                        </StepLabel>
                      </StepperStep>
                    );
                  })}
                </Stepper>
              </Container>
              <Divider />
              <div data-testid="stepper-body">{getStep(onBlur)}</div>
            </div>
            <div className={classes.footer}>
              <Button
                aria-label={t('Cancel Classroom Create')}
                className={classes.cancelButton}
                variant="outlined"
                color="secondary"
                disableElevation
                onClick={handleCancelClick}
              >
                {isFirstStep ? t('Cancel') : t('Back')}
              </Button>
              <Button
                aria-label={t('Step Forward')}
                className={classes.nextButton}
                variant="contained"
                color="primary"
                disableElevation
                onClick={handleForwardClick}
                disabled={isSubmitting || !isValid || (isLastStep && !values.CREATE_CLASS?.name)}
              >
                {isLastStep ? t('Confirm & Create Class') : t('Next Step')}
              </Button>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const useStyles = makeStyles(theme => {
  const STEPPER_HEIGHT = 24;
  const FOOTER_HEIGHT = 80;

  return {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      minHeight: 100,
      paddingLeft: theme.spacing(6)
    },
    stepper: {
      height: STEPPER_HEIGHT,
      paddingTop: 0
    },
    stepLabel: {
      fontWeight: 600
    },
    stepLabelActive: {
      fontWeight: 'unset',
      color: `${theme.palette.secondary.main} !important`
    },
    stepIcon: {
      color: `${theme.palette.secondary.main} !important`
    },
    contentBlock: {
      backgroundColor: theme.palette.divider,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      height: theme.spacing(4) * 10,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    body: {
      height: `calc(100% - ${FOOTER_HEIGHT}px)`,
      overflow: 'auto'
    },
    footer: {
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'center',
      display: 'flex',
      minHeight: FOOTER_HEIGHT,
      borderTop: `1px solid ${theme.palette.divider}`
    },
    nextButton: {
      marginRight: theme.spacing(6)
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    }
  };
});

export default CreateClassroomModal;
